import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCycleStore } from '../../stores/cycleStore';
import { Card, Button } from '../common';
import { useCurrency } from '../../lib/utils/currency';

interface CycleListProps {
  onSelectionChange?: (selectedIds: string[]) => void;
}

export function CycleList({ onSelectionChange }: CycleListProps) {
  const { t } = useTranslation();
  const { symbol } = useCurrency();
  const { cycles, loadCycles, deleteCycle } = useCycleStore();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  useEffect(() => {
    loadCycles();
  }, [loadCycles]);

  const handleToggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const newSelection = prev.includes(id)
        ? prev.filter((selectedId) => selectedId !== id)
        : [...prev, id];
      
      onSelectionChange?.(newSelection);
      return newSelection;
    });
  };

  const handleDeleteClick = (id: string) => {
    setDeleteConfirmId(id);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmId) return;

    try {
      await deleteCycle(deleteConfirmId);
      setSelectedIds((prev) => prev.filter((id) => id !== deleteConfirmId));
      setDeleteConfirmId(null);
    } catch (error) {
      alert(t('comparison.deleteError') + (error instanceof Error ? error.message : t('comparison.unknownError')));
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmId(null);
  };

  const formatCurrency = (value: number) => {
    return `${value.toLocaleString('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${symbol}`;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (cycles.length === 0) {
    return (
      <Card title={t('comparison.savedCycles')}>
        <p className="text-gray-600 dark:text-gray-300 text-center py-8">
          {t('comparison.noCycles')}
        </p>
      </Card>
    );
  }

  return (
    <Card title={t('comparison.savedCycles')}>
      <div className="space-y-3">
        {cycles.map((cycle) => (
          <div
            key={cycle.id}
            className={`border rounded-lg p-4 transition-colors ${
              selectedIds.includes(cycle.id)
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-700'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(cycle.id)}
                  onChange={() => handleToggleSelection(cycle.id)}
                  className="mt-1 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {cycle.name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(cycle.timestamp)}
                  </p>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">FCR:</span>{' '}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {cycle.performance.fcr.toFixed(3)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">EPEF:</span>{' '}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {cycle.performance.epef.toFixed(2)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">{t('comparison.netProfit')}:</span>{' '}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {formatCurrency(cycle.netProfit.netProfit)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">{t('comparison.cycle')}:</span>{' '}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {cycle.performance.cycleDays} {t('results.days')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleDeleteClick(cycle.id)}
                aria-label={t('comparison.deleteCycle')}
              >
                🗑️
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('comparison.deleteCycle')}
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {t('comparison.deleteConfirm')}
            </p>
            <div className="flex space-x-3">
              <Button variant="danger" onClick={handleConfirmDelete} fullWidth>
                {t('comparison.yesDelete')}
              </Button>
              <Button variant="secondary" onClick={handleCancelDelete} fullWidth>
                {t('comparison.cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
